import React, { Component } from 'react';
import { Route, Routes } from 'react-router-dom';
import AppRoutes from './constants/AppRoutes';
import { Layout } from './components/Layout/Layout';
import { history } from './helpers/history';
import AuthVerify from "common/AuthVerify";
import SessionMonitor from "common/SessionMonitor";
import SessionEndedModal from "common/SessionEndedModal";
//import { datadogRum } from '@datadog/browser-rum';

import Intercom from '@intercom/messenger-js-sdk';
import { v4 as uuidv4 } from 'uuid';
import { updateCartDetails } from "actions/cart";
import Roku from "components/Roku/Roku";
import TizenRemote from "components/TizenRemote";
import AppView from "components/AppView/Index";
import ChatBot from './components/ChatBot/ChatBot';

import PrivateRoute from './components/PrivateRoute';
import PlayerList from './components/Room/PlayerList';
import CRMModal from './components/Modals/CRMModal/CRMModal';
import NewGameModal from './components/Modals/NewGameModal/NewGameModal';
import KickstarterModal from "./components/Modals/KickstarterModal/KickstarterModal";
import RedirectListener from './components/Room/RedirectListener';
import Toasts from 'components/Toasts/Toasts';
import Maintenance from './components/Maintenance';
import DotLoader from './components/Utlility/DotLoader';
import { initCapabilitiesReporting, getCachedReport } from "telemetry/capabilitiesReporter.ts";

import Assets from './constants/assets';
import { APP_TYPES } from './constants/AppTypes';

import './custom.css';
import 'shared/fonts.scss';

import { setCountryCode } from "actions/pricing";
import { setLimitAccessFireTv, setAppType, setDeviceCapabilities, setIsAppView } from "actions/devices";
import { connect } from 'react-redux';
import { withRouter } from './components/withRouter';
import { Helmet } from 'react-helmet';
import { setCookie } from './helpers/cookies';
import { getBaseUrl } from 'helpers/url';

import Logo from 'images/logo-high-res.png';
import LogoBlack from 'images/logo-black.png';
import LogoWhite from 'images/logo-white.png';
import AppLayout from './components/AppView/AppLayout';

import { remoteConfig } from './services/remoteConfig';
import { promoConfig } from './services/promoConfig';
import { initializeGames, refreshGames } from './constants/GameListAdapter';

history.listen(({ location, action }) => {
    // Don't scroll to top on back/forward navigation — preserve scroll position
    if (action !== 'POP') {
        window.scrollTo(0, 0);
    }
});

const isDev = !process.env.REACT_APP_ENVIRONMENT || process.env.REACT_APP_ENVIRONMENT === 'development';
const isLan = process.env.REACT_APP_ENVIRONMENT === 'lan'
const isStage = process.env.REACT_APP_ENVIRONMENT === 'stage';


class App extends Component {
    static displayName = App.name;

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            isRedirecting: true,
            isMaintenanceMode: false,
            bannerMessage: null,
        };
        // Check for maintenance bypass
        const params = new URLSearchParams(window.location.search);
        const loginAppType = params.get('loginAppType');
        // Samsung is a restricted TV app like Fire TV (store policy: no in-app
        // registration / external payments), so it gets the same limited access.
        this.limitAccessFireTv = loginAppType === APP_TYPES.FIRE_TV || loginAppType === APP_TYPES.SAMSUNG;
        const bypassCode = params.get('bypass');
        const validBypassCode = process.env.REACT_APP_MAINTENANCE_BYPASS_CODE || 'vocala123';
        this.maintenanceBypass = bypassCode === validBypassCode;

        if (this.maintenanceBypass) {
            console.log('Maintenance mode bypassed');
            // Store bypass in session storage so it persists during navigation
            sessionStorage.setItem('maintenanceBypass', 'true');
        } else {
            // Check if bypass was previously set in session
            this.maintenanceBypass = sessionStorage.getItem('maintenanceBypass') === 'true';
        }

        // Escape hatch: ?platform=web clears any persisted TV/app mode so a
        // device that was stuck in FireTV/Roku/Sky/App can be reset.
        if (params.get('platform') === 'web') {
            localStorage.removeItem('appType');
            localStorage.removeItem('IsAppView');
        }

        // Connect/mirror clients get bounced to /firetv or /roku when a game
        // ends because each game builds the redirect URL from the room host's
        // app type. The mirror still has ?mirror=ROOMCODE in their URL — catch
        // that here, scrub any persisted TV state, and forward to /lobby where
        // doMirrorJoin handles the reconnect. Without this the path resolves
        // to FIRE_TV/ROKU/SKY and render() drops them into the locked TV view.
        const mirrorRoomCode = params.get('mirror');
        if (mirrorRoomCode && this.resolveAppTypeByPath(window.location.pathname) !== APP_TYPES.WEB) {
            localStorage.removeItem('appType');
            localStorage.removeItem('IsAppView');
            const lobbyParams = new URLSearchParams();
            lobbyParams.set('mirror', mirrorRoomCode);
            const mirrorToken = params.get('token');
            if (mirrorToken) lobbyParams.set('token', mirrorToken);
            window.location.replace(`/lobby?${lobbyParams.toString()}`);
            return;
        }

        this.isApp = this.detectAppLaunch();
        this.appType = this.isApp ? APP_TYPES.MOBILE_APP : this.resolveAppTypeByPath(window.location.pathname)

        if (this.isApp) {
            localStorage.setItem("IsAppView", "true");
            if (window.webkit?.messageHandlers?.logHandler) {
                window.webkit.messageHandlers.logHandler.postMessage(`isAppView set true`)
            }
        } else if (localStorage.getItem("IsAppView") === "true") {
            this.appType = APP_TYPES.MOBILE_APP;
        }

        // Persist TV app types so a redirect back from a game (e.g. /play?token=X)
        // doesn't drop the FireTV/Roku/Sky context and dump the host onto the web view.
        const TV_APP_TYPES = [APP_TYPES.FIRE_TV, APP_TYPES.SAMSUNG, APP_TYPES.ROKU, APP_TYPES.SKY];
        if (TV_APP_TYPES.includes(this.appType)) {
            localStorage.setItem('appType', this.appType);
        } else if (this.appType === APP_TYPES.WEB) {
            const savedAppType = localStorage.getItem('appType');
            if (TV_APP_TYPES.includes(savedAppType)) {
                this.appType = savedAppType;
            }
        }


        // Only initialize Intercom if NOT a TV app
        if (![APP_TYPES.ROKU, APP_TYPES.FIRE_TV, APP_TYPES.SAMSUNG, APP_TYPES.SKY].includes(this.appType) && !this.limitAccessFireTv) {
            Intercom({
                app_id: 'fsbgyo6s',
            });
        }

        if (window.webkit?.messageHandlers?.logHandler) {
            window.webkit.messageHandlers.logHandler.postMessage(`App loade ${window.location.pathname}, ${this.isApp}`)
        }
    }

    detectAppLaunch() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('app') === 'native') {
            return true;
        }

        const userAgent = navigator.userAgent || '';
        if (userAgent.includes('GameCityApp')) {
            return true;
        }

        if (window.location.pathname.includes('/app')) {
            return true;
        }

        return false;
    }

    resolveAppTypeByPath(pathname) {
        const lowerPath = pathname.toLowerCase();
        for (const type of Object.values(APP_TYPES)) {
            if (lowerPath.includes(`/${type}`)) {
                return type;
            }
        }
        return APP_TYPES.WEB;
    }

    componentDidMount() {
        console.log('=== DEVICE VALUES ===');
        console.log('limitAccessFireTv:', this.limitAccessFireTv);
        console.log('appType:', this.appType);
        console.log('isApp:', this.isApp);
        console.log('===================');

        // Set device-related state in Redux
        console.log('Dispatching setLimitAccessFireTv...');
        this.props.setLimitAccessFireTv(this.limitAccessFireTv);
        console.log('Dispatching setAppType...');
        this.props.setAppType(this.appType);
        console.log('Dispatching setIsAppView...');
        this.props.setIsAppView(this.isApp);

        if (!this.props.pricing.countryCode) {
            this.props.setCountryCode();
        }
        if (this.appType === APP_TYPES.MOBILE_APP && window.location.pathname === '/app') {
            window.location.replace('/join?forcecache=220425');
            return;
        } else {
            this.setState({ isRedirecting: false });
        }



        var uniqueId = localStorage.getItem("uniqueId");
        console.log("UniqueId:", uniqueId)
        if (!uniqueId) {
            uniqueId = uuidv4();
            localStorage.setItem('uniqueId', uniqueId);
            setCookie('uniqueId', uniqueId, 365);
        }

        const params = new URLSearchParams(window.location.search);
        const src = params.get('src');
        if (src) {
            setCookie('purchase_src', src, 30);
        }

        // Load everything in parallel
        Promise.all([
            // Game list initialization from API (with static fallback)
            // Pass countryCode for regional tag scoping. May still be null
            // here if the IP lookup hasn't resolved — componentDidUpdate
            // refetches once it lands so the homepage best-sellers reflect
            // the user's region without a manual reload.
            initializeGames(false, this.props.pricing?.countryCode).then(games => {
                console.log(`GameListAdapter: ${games.length} games loaded`);
                if (this.props.cart?.items?.length > 0) {
                    this.props.updateCartDetails();
                }
            }).catch(error => {
                console.warn("Games API unavailable, using static fallback:", error);
            }),

            // Remote config loading
            remoteConfig.load().then(config => {
                this.setState({
                    isMaintenanceMode: remoteConfig.isMaintenanceMode(),
                    bannerMessage: remoteConfig.getBannerMessage()
                });
                console.log("Remote config loaded:", config);
            }).catch(error => {
                console.error("Error loading remote config:", error);
                // Continue loading even if remote config fails
                this.setState({
                    isMaintenanceMode: false,
                    bannerMessage: ''
                });
            }),

            // Promo "Top Sellers" campaign config (graceful default if unavailable).
            // Region may still be null here — componentDidUpdate refetches once the
            // countryCode lands so the hero reflects the user's region.
            promoConfig.load(this.props.pricing?.countryCode).then(config => {
                console.log("Promo config loaded:", config);
            }).catch(error => {
                console.warn("Error loading promo config, using defaults:", error);
            }),

            // Capabilities reporting
            initCapabilitiesReporting({
                uniqueId,
                deferIdle: true,
                extra: {
                    env: process.env.REACT_APP_ENVIRONMENT || "dev",
                    appType: this.appType,
                    isAppView: this.isApp,
                },
            }).then(() => {
                const deviceCapabilities = getCachedReport();
                this.props.setDeviceCapabilities(deviceCapabilities);
                console.log("Capabilities ready:", deviceCapabilities);
            }),

            //Image preloading
            Promise.all(Assets.images.map(image => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = image;
                });
            })),

            //// Font preloading
            Promise.all(Assets.fonts.map(font => {
                return new Promise((resolve, reject) => {
                    const fontFace = new FontFace(font.font, `url(${font.path})`);
                    fontFace.load().then(() => {
                        document.fonts.add(fontFace);
                        resolve();
                    }).catch(reject);
                });
            }))
        ]).then(() => {
            this.setState({ isLoading: false });
        }).catch(error => {
            console.error("Error during loading:", error);
            this.setState({ isLoading: false });
        });
    }

    componentDidUpdate(prevProps) {
        // pricing.countryCode is hydrated asynchronously from the IP lookup
        // after mount. When it transitions from absent to set, refresh the
        // games list so regional tag scoping (best-sellers etc.) reflects
        // the user's actual country instead of the default fallback the
        // first fetch returned.
        const prev = prevProps.pricing?.countryCode;
        const next = this.props.pricing?.countryCode;
        if (prev !== next && next) {
            refreshGames(next).catch(err => {
                console.warn("Games refresh after countryCode resolution failed:", err);
            });
            promoConfig.refetch(next).catch(err => {
                console.warn("Promo config refetch after countryCode resolution failed:", err);
            });
        }
    }

    render() {
        // Show loading screen
        if (this.state.isLoading) {
            return this.state.isRedirecting ? (
                <div className="loadingScreen"></div>
            ) : (
                <div className="loadingScreen">
                    <img src={LogoWhite} style={{ width: "20vh", height: "auto", marginBottom: "3vh" }} alt="loading" />
                    <DotLoader size="large" variant="light" />
                </div>
            );
        }

        // Show maintenance mode for ALL app types (unless bypassed)
        if (this.state.isMaintenanceMode && !this.maintenanceBypass) {
            return <Maintenance />;
        }

        // Normal app rendering based on app type
        if (this.appType === APP_TYPES.MOBILE_APP) {
            return (
                <React.Fragment>
                    <AppView />
                    {/*<ChatBot />*/}
                    <SessionMonitor />
                    <SessionEndedModal />
                    <RedirectListener />
                </React.Fragment>
            );
        }

        if ([APP_TYPES.ROKU, APP_TYPES.FIRE_TV, APP_TYPES.SAMSUNG, APP_TYPES.SKY].includes(this.appType)) {
            return (
                <React.Fragment>
                    <Roku appType={this.appType} />
                    <PlayerList isTvApp={true} />
                    <TizenRemote appType={this.appType} />
                    <AuthVerify />
                    <SessionMonitor />
                    <SessionEndedModal />
                    <RedirectListener />
                    {isDev && <span className="dev-badge">DEV</span>}
                    {isStage && <span className="dev-badge">STAGE</span>}
                </React.Fragment>
            );
        }

        // Web app
        return (
            <div style={{ overflow: "hidden" }} >
                <Helmet>
                    {/* Default title — child Helmets (PageSEO) override this. Without a default, react-helmet leaves the previous title in place when a page without its own <title> mounts. */}
                    <title>Game.city — TV Party Games for Your Living Room</title>
                    <link rel="canonical" href={`https://game.city${window.location.pathname}`} />
                    <meta name="robots" content="noindex, follow" />
                    <meta property="og:site_name" content="Game.city" />
                    <meta property="og:type" content="website" />
                    <meta property="og:image" content="https://game.city/logo-og.png" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:image" content="https://game.city/logo-og.png" />
                </Helmet>
                <Layout bannerMessage={this.state.bannerMessage}>
                    <React.Fragment>
                        <Routes >
                            {AppRoutes.map((route, index) => {
                                const { element, ...rest } = route;
                                if (rest.private) {
                                    return <Route exact path={rest.path} key={index} element={<PrivateRoute path={route.path} />}>
                                        <Route exact path={rest.path} key={index} element={element} />
                                    </Route>
                                } else {
                                    return <Route path={rest.path} key={index} {...rest} element={element} />;
                                }
                            })}
                        </Routes>
                        <PlayerList />
                        <Toasts />
                        {!isLan && !this.limitAccessFireTv && <CRMModal />}
                        {/*<KickstarterModal />*/}
                        <RedirectListener />
                        <AuthVerify />
                        <SessionMonitor />
                        <SessionEndedModal />
                        {/*<ChatBot />*/}
                    </React.Fragment>
                </Layout>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        auth: state.auth,
        pricing: state.pricing,
        cart: state.cart,
    };
}

const mapDispatchToProps = (dispatch) => {
    return {
        setCountryCode: () => {
            dispatch(setCountryCode())
        },
        updateCartDetails: () => {
            dispatch(updateCartDetails())
        },
        setLimitAccessFireTv: (value) => {
            dispatch(setLimitAccessFireTv(value))
        },
        setAppType: (appType) => {
            dispatch(setAppType(appType))
        },
        setDeviceCapabilities: (capabilities) => {
            dispatch(setDeviceCapabilities(capabilities))
        },
        setIsAppView: (isApp) => {
            dispatch(setIsAppView(isApp))
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App));